import * as s from '~js/utils/selectors';
import * as a from '~js/utils/attributes';
import * as c from '~js/utils/classes';
import * as stngs from '~js/utils/settings';

const Flickity = themeVendor.Flickity

export function debounce(fn, time) {
  let timeout;
  return function () {
    // eslint-disable-next-line prefer-rest-params
    if (fn) {
      const functionCall = () => fn.apply(this, arguments);
      clearTimeout(timeout);
      timeout = setTimeout(functionCall, time);
    }
  };
}

export const throttle = (fn, wait) => {
  let prev, next;
  return function invokeFn(...args) {
    const now = Date.now();
    next = clearTimeout(next);
    if (!prev || now - prev >= wait) {
      // eslint-disable-next-line prefer-spread
      fn.apply(null, args);
      prev = now;
    } else {
      next = setTimeout(invokeFn.bind(null, ...args), wait - (now - prev));
    }
  };
};

export function getScreenOrientation() {
  if (window.matchMedia('(orientation: portrait)').matches) {
    return 'portrait';
  }

  if (window.matchMedia('(orientation: landscape)').matches) {
    return 'landscape';
  }
}

function getHeight(selector) {
  const el = document.querySelector(selector);
  if (el) {
    return el.clientHeight;
  } else {
    return 0;
  }
}

export function readHeights() {
  const h = {};
  h.windowHeight = Math.min(window.screen.height, window.innerHeight);
  h.footerHeight = getHeight(s.selectors$1b.footer);
  h.headerHeight = getHeight(s.selectors$1b.header);
  h.stickyHeaderHeight = isHeaderSticky() ? window.stickyHeaderHeight : 0;
  h.headerInitialHeight =
    parseInt(
      document.querySelector(s.selectors$1b.header)?.dataset.height ||
        document.querySelector(s.selectors$1b.header)?.offsetHeight
    ) || 0;
  h.announcementBarHeight = getHeight(s.selectors$1b.announcementBar);
  h.collectionStickyBarHeight = getHeight(s.selectors$1b.collectionStickyBar);
  return h;
}

function dispatchResizeEvent() {
  document.dispatchEvent(
    new CustomEvent('theme:resize', {
      bubbles: true,
    })
  );

  if (window.lastWindowWidth !== window.innerWidth) {
    document.dispatchEvent(
      new CustomEvent('theme:resize:width', {
        bubbles: true,
      })
    );

    window.lastWindowWidth = window.innerWidth;
  }
}

export function resizeListener() {
  window.addEventListener('resize', debounce(dispatchResizeEvent, 50));
}

export  function isHeaderSticky() {
  return document.querySelector(s.selectors$1b.stickyHeader);
}

export const makeSwatches = (container) => {
  let swatches = [];
  const els = container.querySelectorAll(s.selectors$X.swatch);
  els.forEach((el) => {
    swatches.push(new Swatch(el));
  });
};


export function getSearchParams(searchForm, filtersForm, deleteParams = [], type = false) {
  const searchFormData = new FormData(searchForm);
  const searchFormParams = new URLSearchParams(searchFormData);

  if (!filtersForm) return searchFormParams.toString();

  const filtersFormData = new FormData(filtersForm);
  const filtersFormParams = new URLSearchParams(filtersFormData);

  // Get keys with empty values from the search-form and filters-form's FormData objects and delete them
  const emptyParams = [];
  for (const pair of searchFormData.entries()) {
    if (pair[1] === '') emptyParams.push(pair[0]);
  }
  for (const pair of filtersFormData.entries()) {
    if (pair[1] === '') emptyParams.push(pair[0]);
  }
  for (let index = 0; index < emptyParams.length; index++) {
    const param = emptyParams[index];
    if (searchFormParams.has(param)) searchFormParams.delete(param);
    if (filtersFormParams.has(param)) filtersFormParams.delete(param);
  }

  // Delete duplicated keys gotten from the filters FormData object
  for (const key of searchFormParams.keys()) {
    if (filtersFormParams.has(key)) filtersFormParams.delete(key);
  }

  // Delete keys from deleteParams array
  if (deleteParams.length > 0) {
    for (let index = 0; index < deleteParams.length; index++) {
      const param = deleteParams[index];
      if (searchFormParams.has(param)) searchFormParams.delete(param);
      if (filtersFormParams.has(param)) filtersFormParams.delete(param);
    }
  }

  // Replace type key if necessary
  if (type) {
    if (filtersFormParams.has('type')) filtersFormParams.delete('type');
    searchFormParams.set('type', type);
  }

  return `${searchFormParams.toString()}&${filtersFormParams.toString()}`;
}

const loaders = {};

export function loadScript(options = {}) {
  if (!options.type) {
    options.type = 'json';
  }

  if (options.url) {
    if (loaders[options.url]) {
      return loaders[options.url];
    } else {
      return getScriptWithPromise(options.url, options.type);
    }
  } else if (options.json) {
    if (loaders[options.json]) {
      return Promise.resolve(loaders[options.json]);
    } else {
      return window
        .fetch(options.json)
        .then((response) => {
          return response.json();
        })
        .then((response) => {
          loaders[options.json] = response;
          return response;
        });
    }
  } else if (options.name) {
    const key = ''.concat(options.name, options.version);
    if (loaders[key]) {
      return loaders[key];
    } else {
      return loadShopifyWithPromise(options);
    }
  } else {
    return Promise.reject();
  }
}

function getScriptWithPromise(url, type) {
  const loader = new Promise((resolve, reject) => {
    if (type === 'text') {
      fetch(url)
        .then((response) => response.text())
        .then((data) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    } else {
      getScript(
        url,
        function () {
          resolve();
        },
        function () {
          reject();
        }
      );
    }
  });

  loaders[url] = loader;
  return loader;
}

function loadShopifyWithPromise(options) {
  const key = ''.concat(options.name, options.version);
  const loader = new Promise((resolve, reject) => {
    try {
      window.Shopify.loadFeatures([
        {
          name: options.name,
          version: options.version,
          onLoad: (err) => {
            onLoadFromShopify(resolve, reject, err);
          },
        },
      ]);
    } catch (err) {
      reject(err);
    }
  });
  loaders[key] = loader;
  return loader;
}

function onLoadFromShopify(resolve, reject, err) {
  if (err) {
    return reject(err);
  } else {
    return resolve();
  }
}

export   function getScript(url, callback, callbackError) {
  let head = document.getElementsByTagName('head')[0];
  let done = false;
  let script = document.createElement('script');
  script.src = url;

  // Attach handlers for all browsers
  script.onload = script.onreadystatechange = function () {
    if (!done && (!this.readyState || this.readyState == 'loaded' || this.readyState == 'complete')) {
      done = true;
      callback();
    } else {
      callbackError();
    }
  };

  head.appendChild(script);
}

// iOS smooth scrolling fix
export function flickitySmoothScrolling(slider) {
  const flkty = Flickity.data(slider);

  if (!flkty) {
    return;
  }

  flkty.on('dragStart', (event, pointer) => {
    document.ontouchmove = function (e) {
      e.preventDefault();
    };
  });

  flkty.on('dragEnd', (event, pointer) => {
    document.ontouchmove = function (e) {
      return true;
    };
  });
}

export const wrap = (toWrap, wrapperClass = '', wrapperOption) => {
  const wrapper = wrapperOption || document.createElement('div');
  wrapper.classList.add(wrapperClass);
  wrapper.setAttribute('data-scroll-lock-scrollable', '');
  toWrap.parentNode.insertBefore(wrapper, toWrap);
  return wrapper.appendChild(toWrap);
};

export function wrapElements(container) {
  // Target tables to make them scrollable
  const tableSelectors = 'table';
  const tables = container.querySelectorAll(tableSelectors);
  tables.forEach((table) => {
    wrap(table, 'table-wrapper');
  });
}

/*
  Remove "is-loading" class to the loaded images and their containers
*/
export function removeLoadingClassFromLoadedImages(container) {
  container.querySelectorAll(s.selectors$19.img).forEach((img) => {
    if (img.complete) {
      img.classList.remove(c.classes$Y.loading);
      img.parentNode.classList.remove(c.classes$Y.loading);

      const section = img.closest(s.selectors$19.section);
      if (section) section.classList.add(c.classes$Y.imgIn);
    }
  });
}

export const showElement = (elem, removeProp = false, prop = 'block') => {
  if (elem) {
    if (removeProp) {
      elem.style.removeProperty('display');
    } else {
      elem.style.display = prop;
    }
  }
};

export const hideElement = (elem) => {
  if (elem) {
    elem.style.display = 'none';
  }
};

export const scrollTo = (elementTop) => {
  const { stickyHeaderHeight } = readHeights();

  window.scrollTo({
    top: elementTop + Math.round(window.scrollY) - stickyHeaderHeight,
    left: 0,
    behavior: 'smooth',
  });
};


export function getWindowWidth() {
  return window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
}

export function isDesktop() {
  return getWindowWidth() >= window.theme.sizes.small;
}

export function isMobile() {
  return getWindowWidth() < window.theme.sizes.small;
}

export   const getScrollbarWidth = () => {
  // Creating invisible container
  const outer = document.createElement('div');
  outer.style.visibility = 'hidden';
  outer.style.overflow = 'scroll'; // forcing scrollbar to appear
  outer.style.msOverflowStyle = 'scrollbar'; // needed for WinJS apps
  document.body.appendChild(outer);

  // Creating inner element and placing it in the container
  const inner = document.createElement('div');
  outer.appendChild(inner);

  // Calculating difference between container's full width and the child width
  const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;

  // Removing temporary elements from the DOM
  outer.parentNode.removeChild(outer);

  return scrollbarWidth;
};

export function singles(frame, wrappers) {
  // sets the height of any frame passed in with the
  // tallest preventOverflowContent as well as any image in that frame
  let tallest = 0;

  wrappers.forEach((wrap) => {
    tallest = wrap.offsetHeight > tallest ? wrap.offsetHeight : tallest;
  });
  const images = frame.querySelectorAll(s.selectors$1a.overflowBackground);
  const frames = [frame, ...images];
  frames.forEach((el) => {
    el.style.setProperty('min-height', `calc(${tallest}px + var(--header-height))`);
  });
}

export function doubles(section) {
  if (window.innerWidth < window.theme.sizes.small) {
    // if we are below the small breakpoint, the double section acts like two independent
    // single frames
    let singleFrames = section.querySelectorAll(s.selectors$1a.overflowFrame);
    singleFrames.forEach((singleframe) => {
      const wrappers = singleframe.querySelectorAll(s.selectors$1a.overflowContent);
      singles(singleframe, wrappers);
    });
    return;
  }

  let tallest = 0;

  const frames = section.querySelectorAll(s.selectors$1a.overflowFrame);
  const contentWrappers = section.querySelectorAll(s.selectors$1a.overflowContent);
  contentWrappers.forEach((content) => {
    if (content.offsetHeight > tallest) {
      tallest = content.offsetHeight;
    }
  });
  const images = section.querySelectorAll(s.selectors$1a.overflowBackground);
  let applySizes = [...frames, ...images];
  applySizes.forEach((el) => {
    el.style.setProperty('min-height', `${tallest}px`);
  });
  section.style.setProperty('min-height', `${tallest}px`);
}

export function preventOverflow(container) {
  const singleFrames = container.querySelectorAll(s.selectors$1a.overflowContainer);
  if (singleFrames) {
    singleFrames.forEach((frame) => {
      const wrappers = frame.querySelectorAll(s.selectors$1a.overflowContent);
      singles(frame, wrappers);
      document.addEventListener('theme:resize', () => {
        singles(frame, wrappers);
      });
    });
  }

  const doubleSections = container.querySelectorAll(s.selectors$1a.overflowWrapper);
  if (doubleSections) {
    doubleSections.forEach((section) => {
      doubles(section);
      document.addEventListener('theme:resize', () => {
        doubles(section);
      });
    });
  }
}

export function isTouchDevice() {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0;
}

export function isTouch() {
  if (isTouchDevice()) {
    document.documentElement.className = document.documentElement.className.replace('no-touch', 'supports-touch');
    window.theme.touch = true;
  } else {
    window.theme.touch = false;
  }
}

export function loading() {
  document.documentElement.classList.remove('is-loading');
  document.documentElement.classList.add('is-loaded');
}

/*
  Catch images loaded events and add class "is-loaded" to them and their containers
*/
export function loadedImagesEventHook() {
  document.addEventListener(
    'load',
    (e) => {
      if (e.target.tagName.toLowerCase() == 'img' && e.target.classList.contains(c.classes$Y.loading)) {
        e.target.classList.remove(c.classes$Y.loading);
        e.target.parentNode.classList.remove(c.classes$Y.loading);

        const section = e.target.closest(s.selectors$19.section);
        if (section) section.classList.add(c.classes$Y.imgIn);
      }
    },
    true
  );
}